import React, { useMemo } from 'react';
import clsx from 'clsx';
import { formatDollars } from '../../utils/formatCurrency';

/**
 * StickyCostSummary — Sticky totals panel for quote/job costing (REQ-SYS-FND-01)
 *
 * Computes read-only derived totals from line items and sticks on scroll.
 * Used in Quote Builder, Job Costing, Change Order, and Invoice views.
 *
 * Usage:
 *   <StickyCostSummary
 *     lineItems={[{ cost: 1200, quantity: 100, margin: 35 }]}
 *     discount={{ type: 'percent', value: 10 }}
 *     options={[{ total: 500 }]}
 *     taxRate={0.0825}
 *   />
 *
 * Or manual mode (pass pre-computed lines):
 *   <StickyCostSummary
 *     lines={[{ label: 'Subtotal', value: '$12,000' }]}
 *     total="$14,500"
 *   />
 */
export default function StickyCostSummary({
  // Auto-compute mode
  lineItems = [],
  discount,
  options = [],
  taxRate = 0,
  // Manual mode
  lines: manualLines,
  total: manualTotal,
  totalLabel = 'Total',
  // Common
  footer,
  className,
}) {
  const computed = useMemo(() => {
    // If manual lines are provided, use them directly
    if (manualLines) return null;

    // Compute from line items
    let subtotal = 0;
    let totalCost = 0;

    lineItems.forEach(item => {
      const qty = Number(item.quantity ?? item.qty ?? item.sqft ?? item.sq_ft ?? 0);
      const unitCost = Number(item.cost || item.unitCost || item.unit_cost || 0);
      const margin = Number(item.margin || item.markup || 0) / 100;
      const additionalLabor = Number(item.additionalLabor || item.additional_labor || 0);
      const additionalCost = Number(item.additionalCost || item.additional_cost || 0);

      // B0: additionalLabor/additionalCost are in DOLLARS (user-entered).
      // Convert to cents to match unitCost * qty which is in cents.
      const lineCost = (unitCost * qty) + (additionalLabor * 100) + (additionalCost * 100);
      const linePrice = margin > 0 ? lineCost / (1 - margin) : lineCost;

      totalCost += lineCost;
      subtotal += linePrice;
    });

    // Discount
    let discountAmount = 0;
    if (discount) {
      if (discount.type === 'percent' || discount.type === '%') {
        discountAmount = subtotal * (Number(discount.value || 0) / 100);
      } else {
        discountAmount = Number(discount.value || discount.amount || 0);
      }
    }

    const afterDiscount = subtotal - discountAmount;

    // Tax
    const taxAmount = afterDiscount * (Number(taxRate) || 0);

    // Options (add-on packages, not in base bid)
    const optionsTotal = options.reduce((sum, opt) => sum + Number(opt.total || opt.price || 0), 0);

    // Grand total
    const grandTotal = afterDiscount + taxAmount;

    // Margin
    const grossMargin = subtotal > 0 ? ((subtotal - totalCost) / subtotal) * 100 : 0;

    return {
      subtotal,
      totalCost,
      discountAmount,
      afterDiscount,
      taxAmount,
      optionsTotal,
      grandTotal,
      grossMargin,
    };
  }, [lineItems, discount, options, taxRate, manualLines]);

  // Build display lines
  const displayLines = manualLines || [
    { label: 'Subtotal', value: formatDollars(computed?.subtotal) },
    computed?.discountAmount > 0 && { label: 'Discount', value: `-${formatDollars(computed.discountAmount)}`, tone: 'success' },
    computed?.taxAmount > 0 && { label: 'Tax', value: formatDollars(computed.taxAmount) },
    computed?.optionsTotal > 0 && { label: 'Options', value: formatDollars(computed.optionsTotal), muted: true },
    { label: 'Margin', value: `${(computed?.grossMargin || 0).toFixed(1)}%`, muted: true },
  ].filter(Boolean);

  const displayTotal = manualTotal || formatDollars(computed?.grandTotal);

  return (
    <aside
      className={clsx('sticky top-4 rounded-[var(--radius-lg)] border p-4', className)}
      style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)', boxShadow: 'none' }}
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--color-text-muted)' }}>
        Estimate
      </h4>
      <dl className="space-y-1.5">
        {displayLines.map((l, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <dt style={{ color: l.muted ? 'var(--color-text-muted)' : 'var(--color-text-default)' }}>
              {l.label}
            </dt>
            <dd style={{
              color: l.tone === 'success' ? 'var(--color-success)' : 'var(--color-text-default)',
              fontWeight: l.tone ? 500 : 400,
            }}>
              {l.value}
            </dd>
          </div>
        ))}
      </dl>
      <div
        className="mt-3 pt-3 flex items-center justify-between border-t"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>
          {totalLabel}
        </span>
        <span className="text-lg font-bold" style={{ color: 'var(--color-text-default)' }}>
          {displayTotal}
        </span>
      </div>
      {footer && <div className="mt-3">{footer}</div>}
    </aside>
  );
}
